import { useState } from 'react';
import { usePlatform } from '@localagent/platform';
import { Button } from '@localagent/ui';
import { toast } from '@localagent/ui/components/sonner';
import { uuidV4, type WorkspaceProjection } from '@localagent/sdk';
import { useReassignWorkstation } from '../../hooks/useWorkspaces';

/**
 * Move a local workspace onto THIS computer when the one it lives on is not
 * connected (workspace_state ADR-028). Deliberately explicit: reattaching on
 * the strength of a path that happens to exist here would bind the workspace to
 * the wrong machine with no action from the person.
 */
/**
 * Whether the move is worth offering. Kept OUT of the component so a host that
 * is not offering it never mounts one — the control needs a platform with a
 * directory picker, and requiring that of every caller would make an unrelated
 * render fail.
 */
export function canMoveWorkspaceHere(projection: WorkspaceProjection): boolean {
  return (
    projection.assignment.routing_class === 'local' &&
    projection.workstation.status !== 'connected' &&
    projection.actions.reassign_workstation.available
  );
}

export function WorkspaceMoveControl({ projection }: { projection: WorkspaceProjection }) {
  const platform = usePlatform();
  const reassign = useReassignWorkstation(projection.workspace_id);
  const [busy, setBusy] = useState(false);

  if (!canMoveWorkspaceHere(projection) || typeof platform.selectDirectory !== 'function') {
    return null;
  }

  const move = async () => {
    setBusy(true);
    try {
      const folderPath = await platform.selectDirectory();
      if (!folderPath) return;
      await reassign.mutateAsync({ folderPath, idempotencyKey: uuidV4() });
      toast.success('Workspace moved to this computer.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not move this workspace');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 rounded-md bg-amber-500/10 p-3" data-testid="workspace-move">
      <p className="text-sm">
        This workspace lives on a computer that is not connected. You can move it to this computer
        and pick the folder it should use here.
      </p>
      <Button
        className="mt-2"
        size="sm"
        disabled={busy}
        onClick={() => void move()}
        data-testid="workspace-move-submit"
      >
        {busy ? 'Moving…' : 'Move to this computer'}
      </Button>
    </div>
  );
}
